import { useRef, useState } from 'react';
import { Link } from 'react-router';
import {
  CalendarOff,
  Calculator,
  Check,
  CircleAlert,
  Clock,
  Lock,
  Pencil,
  Plus,
  RotateCcw,
  Send,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { clsx } from 'clsx';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/dialog/ConfirmDialog';
import { CheckboxField } from '@/components/form/CheckboxField';
import { ListStatus } from '@/components/list/ListStatus';
import { NoCompanySelected } from '@/components/list/NoCompanySelected';
import { Panel } from '@/components/Panel';
import { ActionsMenu, RowActionsMenu } from '@/components/table/RowActionsMenu';
import { FormDialog } from '@/components/dialog/FormDialog';
import { TextField } from '@/components/form/TextField';
import { Button } from '@/components/ui/button';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { useLoader } from '@/hooks/useLoader';
import { getApiErrorMessage } from '@/lib/apiError';
import dayjs, { DATE_TIME_FORMAT } from '@/lib/dayjs';
import { formatMinutes } from '@/lib/duration';
import TimeEntryModel from '@/models/entities/TimeEntryModel';
import { memberUrn } from '@/models/entities/MemberModel';
import { periodUrn } from '@/models/entities/PeriodModel';
import TimeSheetModel from '@/models/entities/TimeSheetModel';
import { TIME_SHEET_SIGNATURE_TYPE, TIME_SHEET_STATUS } from '@/models/references/references';
import { useReferenceOptions } from '@/stores/useReferenceOptions';
import { signTimeSheet } from '@/components/timesheet/signTimeSheet';
import { useCompanyStore, useCompanyTimezone, useCurrentCompany } from '@/stores/useCompanyStore';
import { endInstantOf, instantOf, rangeMinutes, todayIn } from '@/lib/companyTime';
import { loadTimesheet, timesheetKey, type TimesheetData } from './timesheetData';
import {
  DurationPill,
  TimeEntryCard,
  TimeEntryCards,
  TimeEntryCells,
  TimeEntryHead,
} from '@/components/timesheet/TimeEntryTable';
import { TimeSheetStatusBadge } from '@/components/timesheet/TimeSheetStatusBadge';
import { AbsenceBadge } from '@/components/timesheet/AbsenceBadge';
import { AuthorName } from '@/components/AuditDate';
import { TimeSheetStatusHistoryBox } from '@/components/timesheet/TimeSheetStatusHistoryBox';
import { useDataVersion } from '@/hooks/useDataVersion';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { SignDialog } from '@/components/timesheet/SignDialog';

/** Valeurs saisies d'une ligne (date « 2026-10-05 », heures « 18:00 » à l'heure de la société), envoyées en instants */
interface EntryValues {
  date: string;
  startTime: string;
  endTime: string;
  project: string;
  comment: string;
}

/** Ligne en cours de saisie : une nouvelle (new) ou une existante (son id) ; une seule à la fois */
interface Editing {
  entryId: number | 'new';
  values: EntryValues;
}

/**
 * Feuille d'heure (§2.2, §2.3) : déclaration des heures supplémentaires de l'employé sur la période ouverte de la
 * société sélectionnée. Pas de feuille d'avance : elle est créée en brouillon au premier besoin (première ligne, ou
 * soumission « aucune heure », POST /time_sheets). Lignes enregistrées une par une ;
 * soumission confirmée par l'employé (sa signature viendra avec Signature / Xodo Sign), sans retour possible :
 * une fois soumise, la feuille est en lecture seule (le retour en brouillon passera par le refus du manager ou du
 * Dirigeant). Sans équipe active dans la société, pas de déclaration (l'API refuserait la soumission).
 * « Vider la feuille » (pas d'heures sup finalement) supprime toutes les lignes du brouillon en une requête, tout ou rien
 * (DELETE /time_entries?timeSheet=). L'historique du statut de la feuille est affiché dessous. Une absence (déclarée par
 * le manager ou le Dirigeant à la place des heures) est signalée comme telle, avec un lien vers la demande à signer.
 * Réservée aux employés et assistants de la société (navigation) : le Dirigeant ne déclare pas d'heures.
 * Sur mobile : lignes en cartes, saisie dans un panneau bas, total et actions (Ajouter, Soumettre) dans une barre fixe.
 */
export function TimesheetPage() {
  const company = useCurrentCompany();
  const companiesLoaded = useCompanyStore((state) => state.loaded);

  if (companiesLoaded && !company) return <NoCompanySelected />;
  if (!company?.memberId) return <ListStatus loading />;

  // Autre société : autre feuille, saisie en cours abandonnée
  const key = timesheetKey(company.id, company.memberId);
  return <TimesheetLoader key={key} loadKey={key} companyName={company.name} memberId={company.memberId} />;
}

function TimesheetLoader({
  loadKey,
  companyName,
  memberId,
}: {
  loadKey: string;
  companyName: string;
  memberId: number;
}) {
  const { data, error, loaded, reload } = useLoader(loadKey, loadTimesheet);

  if (!loaded || error || !data) return <ListStatus loading={!loaded} error={error} />;

  if (!data.period) {
    return (
      <Panel className="p-6 text-sm text-slate-500">
        Aucune période ouverte pour {companyName} : vous ne pouvez pas déclarer d'heures pour l'instant.
      </Panel>
    );
  }
  // Une feuille déjà soumise reste consultable, même sortie de son équipe depuis
  if (!data.inTeam && (!data.sheet || data.sheet.isDraft)) {
    return (
      <Panel role="status" className="p-6 flex items-start gap-3 text-sm text-slate-600">
        <Users className="w-5 h-5 text-slate-400 shrink-0" />
        <p>
          Vous n'êtes dans aucune équipe de {companyName} : vous ne pouvez pas déclarer d'heures. Contactez votre
          Dirigeant pour être ajouté à une équipe.
        </p>
      </Panel>
    );
  }

  return <TimesheetForm data={data} memberId={memberId} reload={reload} />;
}

function TimesheetForm({ data, memberId, reload }: { data: TimesheetData; memberId: number; reload: () => void }) {
  const { period, sheet, entries, submittedAt, submittedBy, rejection } = data;
  // Historique rechargé avec la feuille (après une soumission, un ajout…)
  const historyVersion = useDataVersion(data);
  const timezone = useCompanyTimezone();
  const isMobile = useIsMobile();
  const [editing, setEditing] = useState<Editing | null>(null);
  const [noHours, setNoHours] = useState(false);
  const [dialog, setDialog] = useState<
    { type: 'submit' } | { type: 'delete'; entry: TimeEntryModel } | { type: 'clear' } | null
  >(null);

  // Pas encore de feuille : un brouillon à venir
  const isDraft = !sheet || sheet.isDraft;
  const isAbsence = !!sheet?.isAbsence;
  const draftLabel = useReferenceOptions('timeSheetStatuses').find(
    (status) => status.reference === TIME_SHEET_STATUS.DRAFT,
  )?.label;
  // Feuille créée au premier besoin (POST) ; gardée le temps du rechargement, pour ne pas la créer deux fois
  const created = useRef<string | null>(null);
  const sheetUrn = async (): Promise<string> => {
    if (sheet) return sheet.urn;
    if (!created.current) {
      const { model } = await new TimeSheetModel().save(
        { member: memberUrn(memberId), period: periodUrn(period!.id) },
        { silentError: true },
      );
      created.current = (model as TimeSheetModel).urn;
    }
    return created.current;
  };
  const totalMinutes = entries.reduce((total, entry) => total + entry.durationMinutes, 0);
  const canSubmit = isDraft && !editing && (entries.length > 0 || noHours);
  const addEntry = () => {
    setNoHours(false);
    setEditing({ entryId: 'new', values: EMPTY_VALUES });
  };
  const editEntry = (entry: TimeEntryModel) =>
    setEditing({ entryId: Number(entry.id), values: valuesOf(entry, timezone) });

  const saveEntry = async ({ entryId, values }: Editing) => {
    const attributes = {
      // Instants UTC des heures saisies à l'heure de la société
      startAt: instantOf(values.date, values.startTime, timezone),
      endAt: endInstantOf(values.date, values.endTime, timezone),
      // Facultatifs : vides => null
      project: values.project.trim() || null,
      comment: values.comment.trim() || null,
    };
    if (entryId === 'new') {
      await new TimeEntryModel().save({ timeSheet: await sheetUrn(), ...attributes }, { silentError: true });
    } else {
      await new TimeEntryModel({ id: entryId }).save(attributes, { silentError: true });
    }
    reload();
    setEditing(null);
  };

  return (
    // Mobile, brouillon : place sous le contenu pour la barre d'actions fixe
    <div className={clsx('space-y-6', isMobile && isDraft && 'pb-20')}>
      <Panel className="p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="space-y-1">
          <h2 className="text-lg font-bold text-slate-900 flex flex-wrap items-center gap-2">
            <Clock className="w-5 h-5 text-blue-600" />
            {period!.monthLabel}
            <TimeSheetStatusBadge
              reference={sheet?.timeSheetStatusReference ?? TIME_SHEET_STATUS.DRAFT}
              label={sheet?.timeSheetStatusLabel ?? draftLabel ?? ''}
            />
            {isAbsence && <AbsenceBadge />}
          </h2>
          <p className="text-sm text-slate-500">
            {isDraft ? (
              'Saisissez vos heures supplémentaires ligne par ligne, puis soumettez votre feuille.'
            ) : isAbsence ? (
              <>
                {/* Absence soumise au nom de celui qui l'a déclarée (dernière ligne Soumise de l'historique) */}
                Absence déclarée{submittedAt ? ` le ${submittedAt.format(DATE_TIME_FORMAT)}` : ''}
                {submittedBy && (
                  <>
                    {' '}
                    par <AuthorName by={submittedBy} />
                  </>
                )}
                .
              </>
            ) : (
              `Soumise${submittedAt ? ` le ${submittedAt.format(DATE_TIME_FORMAT)}` : ''}${sheet?.teamName ? `, équipe ${sheet.teamName}` : ''}.`
            )}
          </p>
        </div>
        {/* Mobile, brouillon : le total est dans la barre d'actions fixe */}
        {entries.length > 0 && !(isMobile && isDraft) && (
          <p
            className="flex items-center gap-2 text-blue-700 bg-blue-50 px-3 py-1 rounded-full text-sm font-semibold"
            data-testid="timesheet-total"
          >
            <Calculator className="w-4 h-4" />
            Total : {formatMinutes(totalMinutes)}
          </p>
        )}
      </Panel>

      <Panel className="overflow-hidden">
        {rejection && (
          <div
            role="alert"
            className="bg-red-50 border-b border-red-200 p-4 flex items-start gap-3 text-sm text-red-800"
          >
            <CircleAlert className="w-5 h-5 text-red-500 shrink-0" />
            <div>
              <p className="font-semibold">
                Votre feuille a été refusée le {rejection.createdAt.format(DATE_TIME_FORMAT)} : corrigez-la puis
                soumettez-la de nouveau.
              </p>
              <p className="mt-1 whitespace-pre-line" data-testid="rejection-comment">
                Motif : {rejection.comment}
              </p>
            </div>
          </div>
        )}
        {isAbsence && (
          <div
            role="status"
            className="bg-violet-50 border-b border-violet-200 p-4 flex flex-wrap items-center gap-3 text-sm text-violet-800"
          >
            <CalendarOff className="w-5 h-5 text-violet-500 shrink-0" />
            <span className="flex-1">
              Une absence a été déclarée pour vous sur cette période : vous n'avez pas d'heures à déclarer.
            </span>
            <Link to={`/requests/${sheet!.id}`} className="font-semibold hover:underline">
              Voir la demande
            </Link>
          </div>
        )}
        {!isDraft && !isAbsence && (
          <div className="bg-slate-50 border-b border-slate-200 p-4 flex items-center gap-3 text-sm text-slate-600">
            <Lock className="w-5 h-5 text-slate-400 shrink-0" />
            Votre feuille est soumise : elle ne peut plus être modifiée. Contactez votre manager en cas d'erreur.
          </div>
        )}

        {/* Sur mobile, la nouvelle ligne se saisit dans le panneau bas, pas dans la liste */}
        {entries.length === 0 && (isMobile || editing?.entryId !== 'new') ? (
          <div className="p-8 flex flex-col items-center text-center gap-4 bg-slate-50/30">
            <p className="text-sm text-slate-500">
              {isDraft
                ? "Aucune ligne d'heure saisie pour cette période."
                : isAbsence
                  ? 'Absence : aucune heure à déclarer.'
                  : 'Aucune heure supplémentaire déclarée.'}
            </p>
            {isDraft && (
              <CheckboxField
                id="no-hours"
                label="Je ne déclare aucune heure supplémentaire pour cette période"
                checked={noHours}
                onChange={setNoHours}
              />
            )}
          </div>
        ) : isMobile ? (
          <TimeEntryCards>
            {entries.map((entry) => (
              <EntryCard
                key={entry.id}
                entry={entry}
                actions={isDraft && !editing}
                onEdit={() => editEntry(entry)}
                onDelete={() => setDialog({ type: 'delete', entry })}
              />
            ))}
          </TimeEntryCards>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[900px]">
              <TimeEntryHead actions={isDraft} />
              <tbody className="divide-y divide-slate-200">
                {entries.map((entry) =>
                  editing?.entryId === Number(entry.id) ? (
                    <EntryEditor key={entry.id} editing={editing} onChange={setEditing} onSave={saveEntry} />
                  ) : (
                    <EntryRow
                      key={entry.id}
                      entry={entry}
                      actions={isDraft && !editing}
                      onEdit={() => editEntry(entry)}
                      onDelete={() => setDialog({ type: 'delete', entry })}
                    />
                  ),
                )}
                {editing?.entryId === 'new' && (
                  <EntryEditor editing={editing} onChange={setEditing} onSave={saveEntry} />
                )}
              </tbody>
            </table>
          </div>
        )}

        {isDraft && !isMobile && (
          <div className="p-4 border-t border-slate-200 bg-slate-50/50 flex flex-wrap justify-between items-center gap-3">
            <Button
              variant="ghost"
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              disabled={!!editing}
              onClick={addEntry}
            >
              <Plus />
              Ajouter une ligne
            </Button>
            <div className="flex flex-wrap items-center gap-3">
              {sheet && entries.length > 0 && (
                <ClearButton disabled={!!editing} onClick={() => setDialog({ type: 'clear' })} />
              )}
              <Button disabled={!canSubmit} onClick={() => setDialog({ type: 'submit' })}>
                <Send />
                Soumettre
              </Button>
            </div>
          </div>
        )}
        {/* Mobile : action secondaire sous la liste, les principales dans la barre fixe */}
        {isDraft && isMobile && sheet && entries.length > 0 && (
          <div className="p-3 border-t border-slate-200 bg-slate-50/50 flex justify-center">
            <ClearButton disabled={!!editing} onClick={() => setDialog({ type: 'clear' })} />
          </div>
        )}
      </Panel>

      {isDraft && isMobile && (
        <div
          className="fixed inset-x-0 bottom-0 z-30 bg-card border-t border-slate-200 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex items-center gap-2"
          data-testid="timesheet-action-bar"
        >
          <p className="flex-1 min-w-0 text-sm">
            <span className="block text-xs text-slate-500">Total</span>
            <span className="font-bold text-slate-900">{formatMinutes(totalMinutes)}</span>
          </p>
          <Button
            variant="outline"
            size="lg"
            className="h-11"
            aria-label="Ajouter une ligne"
            disabled={!!editing}
            onClick={addEntry}
          >
            <Plus />
            Ajouter
          </Button>
          <Button size="lg" className="h-11" disabled={!canSubmit} onClick={() => setDialog({ type: 'submit' })}>
            <Send />
            Soumettre
          </Button>
        </div>
      )}
      {isMobile && editing && <EntrySheet editing={editing} onChange={setEditing} onSave={saveEntry} />}

      {/* Mobile : secondaire (statut dans l'en-tête, refus dans le bandeau), replié et chargé à l'ouverture */}
      {sheet && <TimeSheetStatusHistoryBox sheetId={sheet.id} version={historyVersion} collapsed={isMobile} />}

      {dialog?.type === 'submit' && (
        <SignDialog
          title="Confirmer la soumission"
          description={
            entries.length ? (
              <>
                Vous allez soumettre un total de{' '}
                <strong className="text-slate-900">{formatMinutes(totalMinutes)}</strong> d'heures supplémentaires pour{' '}
                {period!.monthLabel.toLowerCase()}. Une fois soumise, votre feuille ne pourra plus être modifiée.
              </>
            ) : (
              <>
                Vous déclarez <strong className="text-slate-900">aucune heure</strong> supplémentaire pour{' '}
                {period!.monthLabel.toLowerCase()}. Une fois soumise, votre feuille ne pourra plus être modifiée.
              </>
            )
          }
          confirmLabel="Confirmer et envoyer"
          confirmIcon={Send}
          // Soumission : signature de l'employé
          onSign={async (image) => signTimeSheet(await sheetUrn(), TIME_SHEET_SIGNATURE_TYPE.EMPLOYEE, image)}
          onDone={() => {
            setDialog(null);
            toast.success("Votre feuille d'heures a été soumise.");
            reload();
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.type === 'clear' && sheet && (
        <ConfirmDialog
          title="Vider la feuille"
          description={
            <>
              {entries.length > 1 ? `Vos ${entries.length} lignes` : 'Votre ligne'} (
              <strong className="text-slate-900">{formatMinutes(totalMinutes)}</strong>){' '}
              {entries.length > 1 ? 'seront supprimées' : 'sera supprimée'}. Vous pourrez ensuite soumettre votre
              feuille sans heure supplémentaire.
            </>
          }
          confirmLabel="Vider"
          confirmIcon={RotateCcw}
          destructive
          onConfirm={() => new TimeEntryModel().destroyWhere({ timeSheet: sheet.id })}
          onDone={() => {
            setDialog(null);
            toast.success('Votre feuille a été vidée.');
            reload();
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.type === 'delete' && (
        <ConfirmDialog
          title="Supprimer la ligne"
          description={describeEntry(dialog.entry, timezone)}
          confirmLabel="Supprimer"
          confirmIcon={Trash2}
          destructive
          onConfirm={() => new TimeEntryModel({ id: dialog.entry.id }).destroy()}
          onDone={() => {
            setDialog(null);
            reload();
          }}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}

function EntryRow({
  entry,
  actions,
  onEdit,
  onDelete,
}: {
  entry: TimeEntryModel;
  /** Brouillon sans autre saisie en cours */
  actions: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { day } = entry.localIn(useCompanyTimezone());
  return (
    <tr className="text-sm text-slate-700" data-testid="time-entry-row">
      <TimeEntryCells entry={entry} />
      {actions ? (
        <RowActionsMenu label={`Actions de la ligne du ${dayjs(day).format('DD/MM/YYYY')}`}>
          <EntryMenuItems onEdit={onEdit} onDelete={onDelete} />
        </RowActionsMenu>
      ) : (
        <td />
      )}
    </tr>
  );
}

/** Ligne enregistrée en carte (mobile), avec son menu ⋮ dans un brouillon sans autre saisie en cours */
function EntryCard({
  entry,
  actions,
  onEdit,
  onDelete,
}: {
  entry: TimeEntryModel;
  actions: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { day } = entry.localIn(useCompanyTimezone());
  return (
    <TimeEntryCard
      entry={entry}
      actions={
        actions && (
          <ActionsMenu label={`Actions de la ligne du ${dayjs(day).format('DD/MM/YYYY')}`}>
            <EntryMenuItems onEdit={onEdit} onDelete={onDelete} />
          </ActionsMenu>
        )
      }
    />
  );
}

function EntryMenuItems({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  return (
    <>
      <DropdownMenuItem onSelect={onEdit}>
        <Pencil />
        Modifier
      </DropdownMenuItem>
      <DropdownMenuItem variant="destructive" onSelect={onDelete}>
        <Trash2 />
        Supprimer
      </DropdownMenuItem>
    </>
  );
}

function ClearButton({ disabled, onClick }: { disabled: boolean; onClick: () => void }) {
  return (
    <Button
      variant="ghost"
      className="text-red-600 hover:text-red-700 hover:bg-red-50"
      disabled={disabled}
      onClick={onClick}
    >
      <RotateCcw />
      Vider la feuille
    </Button>
  );
}

/**
 * Saisie d'une ligne, commune au tableau et au panneau mobile : champs, état d'envoi et erreur ; save envoie la ligne
 * à l'API, ses refus (chevauchement…) restent affichés
 */
function useEntryForm(
  editing: Editing,
  onChange: (editing: Editing | null) => void,
  onSave: (editing: Editing) => Promise<void>,
) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { values } = editing;
  const set = (field: keyof EntryValues) => (event: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...editing, values: { ...values, [field]: event.target.value } });

  const save = async () => {
    if (!(values.date && values.startTime && values.endTime)) {
      setError('Renseignez la date, le début et la fin.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(editing);
    } catch (e) {
      setError(getApiErrorMessage(e));
      setSaving(false);
    }
  };

  return { values, set, saving, error, save };
}

/** Saisie d'une ligne dans un panneau bas (mobile) : champs empilés et larges, durée calculée en direct */
function EntrySheet({
  editing,
  onChange,
  onSave,
}: {
  editing: Editing;
  onChange: (editing: Editing | null) => void;
  onSave: (editing: Editing) => Promise<void>;
}) {
  const timezone = useCompanyTimezone();
  const { values, set, saving, error, save } = useEntryForm(editing, onChange, onSave);
  const field = 'h-11 text-base';

  return (
    <FormDialog
      sheet
      title={editing.entryId === 'new' ? 'Nouvelle ligne' : 'Modifier la ligne'}
      submitLabel="Enregistrer"
      submitIcon={Check}
      submitting={saving}
      error={error}
      onSubmit={() => void save()}
      onClose={() => !saving && onChange(null)}
    >
      <TextField
        id="entry-date"
        label="Date"
        type="date"
        required
        className={field}
        value={values.date}
        max={todayIn(timezone)}
        onChange={set('date')}
      />
      <div className="grid grid-cols-2 gap-3">
        <TextField
          id="entry-start"
          label="Début"
          type="time"
          required
          className={field}
          value={values.startTime}
          onChange={set('startTime')}
        />
        <TextField
          id="entry-end"
          label="Fin"
          type="time"
          required
          className={field}
          value={values.endTime}
          onChange={set('endTime')}
        />
      </div>
      <p className="flex items-center justify-between text-sm text-slate-500" data-testid="time-entry-duration">
        Durée
        <DurationPill minutes={rangeMinutes(values.date, values.startTime, values.endTime, timezone)} />
      </p>
      <TextField
        id="entry-project"
        label="Projet"
        placeholder="Client, projet ou n° de ticket"
        className={field}
        value={values.project}
        onChange={set('project')}
      />
      <TextField
        id="entry-comment"
        label="Commentaires"
        className={field}
        value={values.comment}
        onChange={set('comment')}
      />
    </FormDialog>
  );
}

/** Saisie d'une ligne dans le tableau : Enregistrer l'envoie à l'API, ses refus (chevauchement…) s'affichent dessous */
function EntryEditor({
  editing,
  onChange,
  onSave,
}: {
  editing: Editing;
  onChange: (editing: Editing | null) => void;
  onSave: (editing: Editing) => Promise<void>;
}) {
  const timezone = useCompanyTimezone();
  const { values, set, saving, error, save: saveEntry } = useEntryForm(editing, onChange, onSave);
  const save = (event: React.FormEvent) => {
    event.preventDefault();
    void saveEntry();
  };

  // Le formulaire est relié aux champs par leur attribut form : un <form> ne peut pas envelopper une ligne de tableau
  const formId = `time-entry-${editing.entryId}`;
  const input = 'h-9 px-2';

  return (
    <>
      <tr className={clsx('bg-blue-50/40', error && 'border-b-0')} data-testid="time-entry-editor">
        <td className="px-4 py-3">
          <Input
            form={formId}
            type="date"
            aria-label="Date"
            aria-required
            className={input}
            value={values.date}
            max={todayIn(timezone)}
            onChange={set('date')}
          />
        </td>
        <td className="px-4 py-3">
          <Input
            form={formId}
            type="time"
            aria-label="Début"
            aria-required
            className={input}
            value={values.startTime}
            onChange={set('startTime')}
          />
        </td>
        <td className="px-4 py-3">
          <Input
            form={formId}
            type="time"
            aria-label="Fin"
            aria-required
            className={input}
            value={values.endTime}
            onChange={set('endTime')}
          />
        </td>
        <td className="px-4 py-3">
          <DurationPill minutes={rangeMinutes(values.date, values.startTime, values.endTime, timezone)} />
        </td>
        <td className="px-4 py-3">
          <Input
            form={formId}
            aria-label="Projet"
            placeholder="Client, projet ou n° de ticket"
            className={input}
            value={values.project}
            onChange={set('project')}
          />
        </td>
        <td className="px-4 py-3">
          <Input
            form={formId}
            aria-label="Commentaires"
            className={input}
            value={values.comment}
            onChange={set('comment')}
          />
        </td>
        <td className="px-4 py-3">
          <form id={formId} onSubmit={save} noValidate className="flex justify-end gap-1">
            <Button type="submit" size="icon" aria-label="Enregistrer la ligne" title="Enregistrer" disabled={saving}>
              <Check />
            </Button>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              aria-label="Annuler la saisie"
              title="Annuler"
              disabled={saving}
              onClick={() => onChange(null)}
            >
              <X />
            </Button>
          </form>
        </td>
      </tr>
      {error && (
        <tr className="bg-blue-50/40">
          <td colSpan={8} className="px-4 pb-3">
            <p role="alert" className="text-sm text-destructive whitespace-pre-line">
              {error}
            </p>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Nouvelle ligne : tout à saisir, date comprise (jamais pré-remplie : le jour même tombe souvent hors de la période
 * ouverte, et une date proposée se valide sans y penser)
 */
const EMPTY_VALUES: EntryValues = { date: '', startTime: '', endTime: '', project: '', comment: '' };

/** Ligne enregistrée remise en saisie, à l'heure de la société */
function valuesOf(entry: TimeEntryModel, timezone: string): EntryValues {
  const { day, startTime, endTime } = entry.localIn(timezone);
  return {
    date: day,
    startTime,
    endTime,
    project: entry.project,
    comment: entry.comment,
  };
}

function describeEntry(entry: TimeEntryModel, timezone: string): string {
  const { day, startTime, endTime } = entry.localIn(timezone);
  return `La ligne du ${dayjs(day).format('DD/MM/YYYY')} (${startTime} – ${endTime}) sera supprimée.`;
}
